/**
 * Confirmation modal for auto-assignment priorities.
 */
import * as React from 'react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Zap } from 'lucide-react';
import { AUTO_ASSIGNMENT_PRIORITIES } from '@/components/pairs/pair-configs';

/** Props for {@link AutoAssignmentModal}. */
interface AutoAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

/** Props for {@link PriorityCard}. */
interface PriorityCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  priority: number;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
}

/** One priority row (icon, rank, title, description). */
const PriorityCard: React.FC<PriorityCardProps> = ({
  icon: Icon,
  title,
  description,
  priority,
  bgColor,
  borderColor,
  textColor,
  iconColor,
}) => {
  return (
    <div
      className="flex items-start gap-4 p-4 rounded-xl border-2 transition-all hover:shadow-md"
      style={{
        background: bgColor,
        borderColor: borderColor,
      }}
    >
      <div
        className="flex items-center justify-center w-12 h-12 rounded-lg shrink-0"
        style={{
          background: `color-mix(in srgb, ${iconColor} 20%, transparent)`,
          border: `2px solid ${iconColor}`,
        }}
      >
        <Icon className="w-6 h-6" style={{ color: iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full"
            style={{
              background: iconColor,
              color: 'white',
            }}
          >
            Priorité {priority}
          </span>
          <h4 className="font-bold text-base" style={{ color: textColor }}>
            {title}
          </h4>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: textColor }}>
          {description}
        </p>
      </div>
    </div>
  );
};

/** Explains auto-assignment priority order before the user confirms. */
export const AutoAssignmentModal: React.FC<AutoAssignmentModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="lg"
      header={{
        icon: Zap,
        iconColor: 'var(--color-2-nuance-1)',
        iconBgColor: 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
        title: 'Affectation Automatique',
        titleColor: 'var(--color-1-nuance-1)',
        titleSize: 'lg',
        titleWeight: 'bold',
        subtitle: "Le système appliquera les priorités suivantes dans l'ordre",
      }}
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <Button preset="modal-cancel" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={Zap}
            iconPosition="left"
            loading={isLoading}
            onClick={onConfirm}
          >
            Lancer l'affectation
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {AUTO_ASSIGNMENT_PRIORITIES.map((item) => (
          <PriorityCard
            key={item.id}
            icon={item.icon}
            title={item.title}
            description={item.description}
            priority={item.priority}
            bgColor={item.bgColor}
            borderColor={item.borderColor}
            textColor={item.textColor}
            iconColor={item.iconColor}
          />
        ))}

        <div
          className="mt-2 p-3 rounded-lg border"
          style={{
            background: 'color-mix(in srgb, var(--color-3-nuance-1) 8%, transparent)',
            borderColor: 'var(--color-3-nuance-1)',
          }}
        >
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Note:</strong> L'algorithme appliquera ces priorités
            automatiquement pour créer les meilleurs matchings possibles. Les affectations manuelles
            existantes seront préservées.
          </p>
        </div>
      </div>
    </BaseModal>
  );
};
